import http from 'hub-http/clients/apiClient';
import quickFetch from 'quick-fetch';
import httpNoPortal from 'hub-http/clients/noPortalApiClient';
import { registerQuery, useQuery } from '../../utils/DataFetchingClient';
import { useCallback, useMemo } from 'react';
import { create } from 'zustand';
import { persist } from 'zustand/middleware';
export const GET_USER_INFO = registerQuery({
  fieldName: 'userInfo',
  args: [],
  fetcher() {
    const earlyMetaRequest = quickFetch.getRequestStateByName('user-info');
    const fetchUserInfo = () => httpNoPortal.get('/login-verify/user-info').then(response => response);
    if (earlyMetaRequest) {
      return new Promise((resolve, reject) => {
        earlyMetaRequest.whenFinished(result => {
          resolve(result);
          quickFetch.removeEarlyRequest('user-info');
        });
        earlyMetaRequest.onError(() => {
          // retry the request
          // eslint-disable-next-line promise/catch-or-return
          fetchUserInfo().then(resolve, reject);
          quickFetch.removeEarlyRequest('user-info');
        });
      });
    }
    return fetchUserInfo();
  }
});
export const getUserInfo = client => {
  return client.queryUnwrapped({
    query: GET_USER_INFO
  });
};
export const useUserInfo = ({
  skip = false
} = {}) => {
  const {
    data: userInfo,
    loading: userInfoLoading,
    error: userInfoError
  } = useQuery(GET_USER_INFO, {
    skip
  });
  return {
    userInfo,
    userInfoLoading,
    userInfoError
  };
};
export const GET_ACCOUNTS = registerQuery({
  fieldName: 'accounts',
  args: [],
  fetcher() {
    return http.get('/accounts/v1/accounts').then(response => response);
  }
});
export const getAccounts = client => {
  return client.queryUnwrapped({
    query: GET_ACCOUNTS
  });
};
export const useAccounts = () => {
  const {
    data: accounts,
    loading: accountsLoading,
    error: accountsError
  } = useQuery(GET_ACCOUNTS);
  let sortedAccounts = null;
  if (accounts) {
    sortedAccounts = [...accounts.accounts].sort((a, b) => {
      return b.numberOfUsers - a.numberOfUsers;
    });
  }
  return {
    accounts: sortedAccounts,
    accountsLoading,
    accountsError
  };
};
export const GET_CROSS_ACCOUNTS = registerQuery({
  fieldName: 'crossAccounts',
  args: [],
  fetcher() {
    const earlyMetaRequest = quickFetch.getRequestStateByName('cross-hublet-accounts');
    const fetchCrossAccounts = () => httpNoPortal.get('/accounts/v1/accounts/cross-hublet').then(response => response);
    if (earlyMetaRequest) {
      return new Promise((resolve, reject) => {
        earlyMetaRequest.whenFinished(result => {
          resolve(result);
          quickFetch.removeEarlyRequest('cross-hublet-accounts');
        });
        earlyMetaRequest.onError(() => {
          // retry the request
          // eslint-disable-next-line promise/catch-or-return
          fetchCrossAccounts().then(resolve, reject);
          quickFetch.removeEarlyRequest('cross-hublet-accounts');
        });
      });
    }
    return fetchCrossAccounts();
  }
});
export const getCrossAccounts = client => {
  return client.queryUnwrapped({
    query: GET_CROSS_ACCOUNTS
  });
};
const GET_USERS_FOR_ACCOUNT = registerQuery({
  fieldName: 'usersForAccount',
  args: ['accountId', 'userIds'],
  fetcher({
    accountId,
    userIds
  }) {
    return http.get('users-query/v1/app/hub-users', {
      query: Object.assign({
        portalId: accountId
      }, (userIds === null || userIds === void 0 ? void 0 : userIds.length) && {
        ids: userIds
      })
    });
  }
});
export const useUsersForAccount = ({
  accountId,
  userIds,
  skip = false
}) => {
  const {
    data: usersForAccount,
    loading: usersForAccountLoading,
    error: usersForAccountError
  } = useQuery(GET_USERS_FOR_ACCOUNT, {
    variables: {
      accountId,
      userIds
    },
    skip
  });
  return {
    usersForAccount,
    usersForAccountLoading,
    usersForAccountError
  };
};
export const GET_GATES_AND_SCOPES = registerQuery({
  fieldName: 'gatesAndScopes',
  args: [],
  fetcher() {
    const earlyMetaRequest = quickFetch.getRequestStateByName('api-verify');
    const defaultResponse = {
      gates: [],
      scopes: []
    };
    if (earlyMetaRequest) {
      return new Promise((resolve, reject) => {
        earlyMetaRequest.whenFinished(result => {
          var _result$portal, _result$portal2;
          resolve({
            gates: [...((result === null || result === void 0 || (_result$portal = result.portal) === null || _result$portal === void 0 ? void 0 : _result$portal.enabled_gates) || []), ...((result === null || result === void 0 || (_result$portal2 = result.portal) === null || _result$portal2 === void 0 ? void 0 : _result$portal2.enabled_v3_gates) || [])],
            scopes: result && result.user && result.user.scopes || []
          });
        });
        earlyMetaRequest.onError(xhr => {
          if (xhr.status === 401) reject(xhr);else {
            resolve(defaultResponse);
          }
        });
      });
    }
    return Promise.resolve(defaultResponse);
  }
});
export const getGatesAndScopes = client => {
  return client.queryUnwrapped({
    query: GET_GATES_AND_SCOPES
  });
};
export const useGatesAndScopes = () => {
  const {
    data: gatesAndScopes,
    loading: gatesAndScopesLoading,
    error: gatesAndScopesError
  } = useQuery(GET_GATES_AND_SCOPES);
  return {
    gatesAndScopes,
    gatesAndScopesLoading,
    gatesAndScopesError
  };
};
export const useGateOverrideStore = create()(persist(set => ({
  setOverride(key, value) {
    set({
      [key]: value
    });
  },
  resetOverride(key) {
    set({
      [key]: undefined
    });
  }
}), {
  name: 'academy:overrideGate'
}));
export const useGateOverride = gateName => {
  var _store$gateName;
  const store = useGateOverrideStore();
  const setOverride = useCallback(value => {
    store.setOverride(gateName, value);
  }, [gateName, store]);
  const resetOverride = useCallback(() => {
    store.resetOverride(gateName);
  }, [gateName, store]);
  return [(_store$gateName = store[gateName]) !== null && _store$gateName !== void 0 ? _store$gateName : null, setOverride, resetOverride];
};
export const useHasGate = gateName => {
  const {
    gatesAndScopes,
    gatesAndScopesError,
    gatesAndScopesLoading
  } = useGatesAndScopes();
  const [gateOverride] = useGateOverride(gateName);
  const hasGate = useMemo(() => {
    if (gateOverride !== null) return gateOverride;
    if (!gatesAndScopes) return null;
    const {
      gates
    } = gatesAndScopes;
    return gates.includes(gateName);
  }, [gateName, gateOverride, gatesAndScopes]);
  return {
    hasGate,
    hasGateError: gatesAndScopesError,
    hasGateLoading: gatesAndScopesLoading
  };
};
export const useScopeOverrideStore = create()(persist(set => ({
  setOverride(key, value) {
    set({
      [key]: value
    });
  },
  resetOverride(key) {
    set({
      [key]: undefined
    });
  }
}), {
  name: 'academy:overrideGate'
}));
export const useScopeOverride = scopeName => {
  var _store$scopeName;
  const store = useScopeOverrideStore();
  const setOverride = useCallback(value => {
    store.setOverride(scopeName, value);
  }, [scopeName, store]);
  const resetOverride = useCallback(() => {
    store.resetOverride(scopeName);
  }, [scopeName, store]);
  return [(_store$scopeName = store[scopeName]) !== null && _store$scopeName !== void 0 ? _store$scopeName : null, setOverride, resetOverride];
};
export const useHasScope = scopeName => {
  const {
    gatesAndScopes,
    gatesAndScopesLoading
  } = useGatesAndScopes();
  const [scopeOverride] = useScopeOverride(scopeName);
  const hasScope = useMemo(() => {
    if (scopeOverride !== null) return scopeOverride;
    if (!gatesAndScopes) return false;
    const {
      scopes
    } = gatesAndScopes;
    return scopes.includes(scopeName);
  }, [scopeName, scopeOverride, gatesAndScopes]);
  return {
    hasScope,
    gatesAndScopesLoading
  };
};
export const useScopes = () => {
  const {
    gatesAndScopes
  } = useGatesAndScopes();
  if (!gatesAndScopes) return null;
  const {
    scopes
  } = gatesAndScopes;
  return scopes;
};
const GET_PORTAL_SCOPES = registerQuery({
  fieldName: 'portalScopes',
  args: ['portalId'],
  fetcher({
    portalId
  }) {
    const earlyMetaRequest = quickFetch.getRequestStateByName('portal-scopes');
    const fetchPortalScopes = () => http.get(`/hubs2/v1/scopes/hub/${portalId}`);
    if (earlyMetaRequest) {
      return new Promise((resolve, reject) => {
        earlyMetaRequest.whenFinished(result => {
          resolve(result);
          quickFetch.removeEarlyRequest('portal-scopes');
        });
        earlyMetaRequest.onError(() => {
          void fetchPortalScopes().then(resolve, reject);
          quickFetch.removeEarlyRequest('portal-scopes');
        });
      });
    }
    return fetchPortalScopes();
  }
});
export const useHasPortalScope = (portalId, scopeName) => {
  const {
    data: portalScopesResponse,
    loading: portalScopesLoading,
    error: portalScopesError
  } = useQuery(GET_PORTAL_SCOPES, {
    variables: {
      portalId: portalId
    },
    skip: !portalId
  });
  const hasPortalScope = useMemo(() => {
    var _portalScopesResponse;
    if (portalScopesLoading) return undefined;
    const portalScopes = (_portalScopesResponse = portalScopesResponse === null || portalScopesResponse === void 0 ? void 0 : portalScopesResponse.scopes) !== null && _portalScopesResponse !== void 0 ? _portalScopesResponse : [];
    if (!portalScopes.length) return false;
    return portalScopes.includes(scopeName);
  }, [portalScopesResponse, scopeName, portalScopesLoading]);
  return {
    hasPortalScope,
    portalScopesLoading,
    portalScopesError
  };
};